import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Loader2, CheckCircle2, XCircle, AlertCircle, ArrowLeft, 
  Clock, Timer, TrendingUp, History, ChevronLeft, ChevronRight,
  BarChart3, Target, BookOpen, Lightbulb, RefreshCw
} from "lucide-react";

/**
 * TGPSC MCQ Practice Page
 * Matches UPSC PrelimsMCQPage UI pattern with exam_type=TGPSC
 * Features: Timer, Performance History, Progressive Loading, Detailed Config
 */

// TGPSC-specific topics for selection
const TGPSC_TOPICS = {
  "Telangana-Specific": [
    "Telangana Movement & State Formation",
    "Kakatiya Dynasty",
    "Qutb Shahi Dynasty", 
    "Nizam Era History",
    "Telangana Geography",
    "Godavari & Krishna Basins",
    "Kaleshwaram Project",
    "Telangana Economy",
    "IT & Pharma Industry",
    "Rythu Bandhu Scheme",
    "Mission Kakatiya"
  ],
  "General Studies": [
    "Indian Polity & Constitution",
    "Indian Economy",
    "Indian Geography",
    "Indian History",
    "Environment & Ecology",
    "Science & Technology",
    "Current Affairs"
  ]
};

const TGPSCMCQPracticePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Form state
  const [topic, setTopic] = useState(searchParams.get("topic") || "");
  const [difficulty, setDifficulty] = useState("medium");
  const [numQuestions, setNumQuestions] = useState(10);
  
  // Quiz state
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  
  // Timer state
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timePerQuestion, setTimePerQuestion] = useState(120); // 2 minutes default
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);
  
  // Performance history
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const isPro = user?.role === "PRO";
  
  // Daily limit for FREE users
  const [dailyUsage, setDailyUsage] = useState(0);
  const FREE_DAILY_LIMIT = 1;

  // Check daily usage on mount
  useEffect(() => {
    if (!isPro) {
      const today = new Date().toISOString().split('T')[0];
      const usageKey = `tgpsc_mcq_daily_usage_${today}`;
      const savedUsage = localStorage.getItem(usageKey);
      setDailyUsage(savedUsage ? parseInt(savedUsage) : 0);
    }
  }, [isPro]);

  // Load performance history
  useEffect(() => {
    const saved = localStorage.getItem('tgpsc_mcq_performance_history');
    if (saved) {
      setPerformanceHistory(JSON.parse(saved));
    }
  }, []);

  // Timer effect
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

  const handleAutoSubmit = () => {
    setTimerActive(false);
    toast({
      variant: "destructive",
      description: "⏰ Time's up! Auto-submitting your answers..."
    });
    setTimeout(() => handleSubmit(), 500);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter or select a topic"
      });
      return;
    }

    // Check daily limit for FREE users
    if (!isPro && dailyUsage >= FREE_DAILY_LIMIT) {
      toast({
        variant: "destructive",
        title: "Daily Limit Reached",
        description: "FREE users can generate 1 MCQ set per day. Upgrade to PRO for unlimited access!"
      });
      navigate("/upgrade");
      return;
    }

    setLoading(true);
    setLoadingMessage("Generating TGPSC-quality MCQs...");
    setQuestions([]);
    setUserAnswers({});
    setShowAnswers(false);
    setCurrentIndex(0);
    setTimerActive(false);

    // Progressive loading messages
    const loadingTimer1 = setTimeout(() => {
      setLoadingMessage("Gathering TGPSC exam patterns & crafting questions...");
    }, 8000);
    
    const loadingTimer2 = setTimeout(() => {
      setLoadingMessage("Creating detailed explanations (this may take up to 1 minute)...");
    }, 20000);
    
    const loadingTimer3 = setTimeout(() => {
      setLoadingMessage("Almost there! Complex topics take a bit longer...");
    }, 40000);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/generate`,
        {
          topic,
          difficulty,
          num_questions: numQuestions,
          mcq_type: "single",
          include_trap_analysis: isPro,
          exam_type: "TGPSC" // Key: Use TGPSC exam context
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minute timeout
        }
      );

      // Clear loading timers
      clearTimeout(loadingTimer1);
      clearTimeout(loadingTimer2);
      clearTimeout(loadingTimer3);

      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
        
        // Start timer if enabled
        if (timerEnabled) {
          setTimeRemaining(timePerQuestion * response.data.questions.length);
          setTimerActive(true);
        }
        
        // Update daily usage for FREE users
        if (!isPro) {
          const today = new Date().toISOString().split('T')[0];
          const usageKey = `tgpsc_mcq_daily_usage_${today}`;
          const newUsage = dailyUsage + 1;
          localStorage.setItem(usageKey, newUsage.toString());
          setDailyUsage(newUsage);
        }
        
        toast({
          description: `Generated ${response.data.questions.length} TGPSC MCQs for "${topic}"`
        });
      } else {
        toast({
          variant: "destructive",
          description: response.data.error || "No questions generated. Try a different topic."
        });
      }
    } catch (err) {
      clearTimeout(loadingTimer1);
      clearTimeout(loadingTimer2);
      clearTimeout(loadingTimer3);
      
      console.error("Error generating MCQs:", err);
      if (err.response?.status === 403) {
        toast({
          variant: "destructive",
          description: "Daily limit reached. Upgrade to PRO for unlimited MCQs."
        });
      } else {
        toast({
          variant: "destructive",
          description: "Failed to generate questions. Please try again."
        });
      }
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleAnswerSelect = (questionIndex, answer) => {
    if (showAnswers) return;
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const handleSubmit = () => {
    setTimerActive(false);
    setShowAnswers(true);
    
    // Calculate score
    let correct = 0;
    questions.forEach((q, idx) => {
      if (userAnswers[idx] === q.correct_answer) {
        correct++;
      }
    });
    
    // Save to performance history
    const newEntry = {
      date: new Date().toISOString(),
      topic,
      difficulty,
      score: correct,
      total: questions.length,
      percentage: Math.round((correct / questions.length) * 100)
    };
    
    const updatedHistory = [newEntry, ...performanceHistory].slice(0, 20);
    setPerformanceHistory(updatedHistory);
    localStorage.setItem('tgpsc_mcq_performance_history', JSON.stringify(updatedHistory));
    
    toast({
      description: `Score: ${correct}/${questions.length} (${newEntry.percentage}%)`
    });
  };

  const handleReset = () => {
    setQuestions([]);
    setUserAnswers({});
    setShowAnswers(false);
    setCurrentIndex(0);
    setTimerActive(false);
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(userAnswers).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-pink-50 to-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="outline" 
          onClick={() => navigate('/tgpsc/prelims')} 
          className="mb-4"
          data-testid="back-to-prelims"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to TGPSC Prelims Hub
        </Button>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 rounded-lg">
              <Target className="w-8 h-8 text-pink-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">TGPSC MCQ Practice</h1>
              <p className="text-slate-600 text-sm">AI-generated questions for Telangana Group-1</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-pink-500">TGPSC</Badge>
            {!isPro && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                {FREE_DAILY_LIMIT - dailyUsage} free left today
              </Badge>
            )}
          </div>
        </div>

        {/* Configuration Card - Show when no questions */}
        {questions.length === 0 && !loading && (
          <Card className="mb-6">
            <CardContent className="p-6 space-y-6">
              {/* Topic Selection */}
              <div>
                <Label className="text-slate-700 font-medium">Topic</Label>
                <Input
                  placeholder="Enter topic or select from below..."
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="mt-2"
                  data-testid="topic-input"
                />
                
                {/* Quick Topic Selection */}
                <div className="mt-4 space-y-4">
                  {Object.entries(TGPSC_TOPICS).map(([category, topics]) => (
                    <div key={category}>
                      <Label className="text-xs text-slate-500 uppercase">{category}</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {topics.map((t) => (
                          <Button
                            key={t}
                            variant={topic === t ? "default" : "outline"}
                            size="sm"
                            onClick={() => setTopic(t)}
                            className={topic === t 
                              ? "bg-pink-600 hover:bg-pink-700" 
                              : "hover:bg-pink-50 hover:border-pink-300"
                            }
                          >
                            {t}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Configuration Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700">Difficulty</Label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full mt-2 px-3 py-2 border rounded-md"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700">Number of Questions</Label>
                  <select
                    value={numQuestions}
                    onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                    className="w-full mt-2 px-3 py-2 border rounded-md"
                  >
                    <option value={5}>5 Questions</option>
                    <option value={10}>10 Questions</option>
                    <option value={15}>15 Questions</option>
                    <option value={20}>20 Questions</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center gap-2">
                    <Timer className="w-4 h-4" />
                    Timer (Optional)
                  </Label>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={timerEnabled}
                      onChange={(e) => setTimerEnabled(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-slate-600">Enable Timer</span>
                  </div>
                  {timerEnabled && (
                    <select
                      value={timePerQuestion}
                      onChange={(e) => setTimePerQuestion(parseInt(e.target.value))}
                      className="w-full mt-2 px-3 py-2 border rounded-md text-sm"
                    >
                      <option value={60}>1 min/question</option>
                      <option value={90}>1.5 min/question</option>
                      <option value={120}>2 min/question</option>
                    </select>
                  )}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={loading || !topic.trim()}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700"
                data-testid="generate-mcqs-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {loadingMessage || "Generating..."}
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 mr-2" />
                    Generate {numQuestions} MCQs
                  </>
                )}
              </Button>

              {/* Performance History Toggle */}
              {performanceHistory.length > 0 && (
                <div className="pt-4 border-t">
                  <Button
                    variant="ghost"
                    onClick={() => setShowHistory(!showHistory)}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Performance History ({performanceHistory.length})
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
                  </Button>
                  
                  {showHistory && (
                    <div className="mt-4 space-y-2">
                      {performanceHistory.slice(0, 5).map((entry, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                          <div>
                            <span className="font-medium">{entry.topic}</span>
                            <span className="text-slate-400 ml-2">({entry.difficulty})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={entry.percentage >= 70 ? "default" : "outline"}>
                              {entry.score}/{entry.total}
                            </Badge>
                            <span className={entry.percentage >= 70 ? "text-green-600" : "text-amber-600"}>
                              {entry.percentage}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <Card>
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-12 h-12 text-pink-600 animate-spin" />
                <p className="text-slate-600 font-medium">{loadingMessage}</p>
                <p className="text-xs text-slate-400">Please don't refresh the page</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quiz Interface */}
        {currentQuestion && !loading && (
          <div className="space-y-4">
            {/* Progress & Timer Bar */}
            <Card className="bg-slate-800 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="border-white text-white">
                      Q {currentIndex + 1} of {questions.length}
                    </Badge>
                    <Badge variant="outline" className="border-pink-400 text-pink-400">
                      {topic}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-400">
                      Answered: {answeredCount}/{questions.length}
                    </span>
                    {timerActive && (
                      <Badge className={`${timeRemaining < 60 ? 'bg-red-500' : 'bg-green-500'}`}>
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTime(timeRemaining)}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Question Card */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-medium text-slate-800 mb-6">
                  {currentQuestion.question}
                </h3>

                {/* Options */}
                <div className="space-y-3">
                  {currentQuestion.options.map((option, idx) => {
                    const isSelected = userAnswers[currentIndex] === option;
                    const isCorrect = option === currentQuestion.correct_answer;
                    
                    let optionClass = "border-slate-200 hover:border-pink-300 hover:bg-pink-50";
                    
                    if (showAnswers) {
                      if (isCorrect) {
                        optionClass = "border-green-500 bg-green-50";
                      } else if (isSelected && !isCorrect) {
                        optionClass = "border-red-500 bg-red-50";
                      }
                    } else if (isSelected) {
                      optionClass = "border-pink-500 bg-pink-50";
                    }

                    return (
                      <button
                        key={idx}
                        onClick={() => handleAnswerSelect(currentIndex, option)}
                        disabled={showAnswers}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all ${optionClass}`}
                        data-testid={`option-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-slate-700">{option}</span>
                          {showAnswers && isCorrect && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                          {showAnswers && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Explanation */}
                {showAnswers && currentQuestion.explanation && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 mb-1">Explanation</p>
                        <p className="text-sm text-slate-700">{currentQuestion.explanation}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>

              {!showAnswers && answeredCount === questions.length && (
                <Button
                  onClick={handleSubmit}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-btn"
                >
                  Submit All Answers
                </Button>
              )}

              {showAnswers && (
                <Button
                  onClick={handleReset}
                  className="bg-pink-600 hover:bg-pink-700"
                  data-testid="new-quiz-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Quiz
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
                disabled={currentIndex === questions.length - 1}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Question Navigator */}
            <Card>
              <CardContent className="p-4">
                <Label className="text-xs text-slate-500 mb-2 block">Quick Navigation</Label>
                <div className="flex flex-wrap gap-2">
                  {questions.map((_, idx) => {
                    const answered = userAnswers[idx] !== undefined;
                    const isCorrect = showAnswers && userAnswers[idx] === questions[idx].correct_answer;
                    const isWrong = showAnswers && answered && !isCorrect;
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                          idx === currentIndex
                            ? "bg-pink-600 text-white"
                            : isCorrect
                            ? "bg-green-500 text-white"
                            : isWrong
                            ? "bg-red-500 text-white"
                            : answered
                            ? "bg-pink-100 text-pink-600 border border-pink-300"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TGPSCMCQPracticePage;
